import { TableDefinition, BfxColDef } from "./TableDefinition";
import { IndexType } from "./IndexType";
import { COLUMN_SELECTION } from "./TableDefinitionFactory.constants";

import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { RateCache } from "../rate/impl/RateCache";

import { BasePricingService } from "../lookup/pricing/impl/BasePricingService";

import { CcyLookupService } from "../lookup/model/impl/CcyLookupService";
import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { CurrencyPairCalendarLookupService } from "../lookup/model/impl/CurrencyPairCalendarLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { RateSourceLookupService } from "../lookup/model/impl/RateSourceLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";

import * as OrdersTableDefinitionFactoryConstants from "./OrdersTableDefinitionFactory.constants";

import { Injectable, Optional } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OrdersTableDefinitionFactory } from "./OrdersTableDefinitionFactory";
import {
  COLUMN_INSTRUMENT,
  COLUMN_BASE_PERCENTAGE,
  COLUMN_ORDER_DEALT_TRACKING,
  COLUMN_DEALT_PERCENTAGE,
  COLUMN_LADDER_POSITION,
} from "./OrdersTableDefinitionFactory.constants";
import { DealtPricingService } from "shared/cubiq-services/lookup/pricing/impl/DealtPricingService";
import { ORDER_LADDER_TABLE_DEFINITION_VER } from "shared/cubiq-services/model/TableDefinitionFactory.constants";
import { LadderRate } from "shared/cubiq-services/model/orders/LadderRate";

import { LadderSpotTrackingHelper } from "./orders/LadderSpotTrackingHelper";
import { Rate } from "../rate/model/Rate";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";
import { OrdersTableMode } from "./OrdersTableMode";
import { ITooltipParams, ValueGetterParams } from "ag-grid-community";
import { PricingService } from "../lookup/pricing/PricingService";
import { BfxSysConfigUtilService } from "shared/auth-services";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";
import { FixingSourceLookupService } from "../lookup/model/impl/FixingSourceLookupService";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";
import { AtdlTooltipUiRep } from "./adtl/atdl-tooltip-ui-rep";
import { LadderPair } from "./orders/LadderPair";
import { LadderAggregate } from "./orders/LadderAggregate";

const ZERO = new CubiqTS.BigNumber("0");

@Injectable()
export class OrderLadderDefinitionFactory extends OrdersTableDefinitionFactory {
  protected defaultSortModel: any = [
    { colId: "instrument", sort: "asc" },
    { colId: COLUMN_BASE_PERCENTAGE, sort: "asc" },
  ];

  protected ladderSpotTrackingHelper: LadderSpotTrackingHelper;

  constructor(
    profileService: ProfilePromiseActionService,
    rateCache: RateCache,
    @Optional() rateSourceLookup: RateSourceLookupService,
    @Optional() myClientLookup: ClientLookupService,
    @Optional() myAccountLookup: AccountLookupService,
    @Optional() mySalesGroupLookup: SalesGroupLookupService,
    @Optional() myTraderGroupLookup: TraderGroupLookupService,
    @Optional() ccyLookupService: CcyLookupService,
    @Optional() ccyPairLookupService: CcyPairLookupService,
    @Optional() userLookupService: UserLookupService,
    @Optional() sysConfigService: SystemConfigPromiseService,
    @Optional() translateService: TranslateService,
    @Optional() myBasePricingService: BasePricingService,
    @Optional() myDealtPricingService: DealtPricingService,
    @Optional() fixingSourceLookupService: FixingSourceLookupService,
    @Optional() algoStrategyLookupService: AlgoStrategyLookupService,
    @Optional()
    currencyPairCalendarLookupService: CurrencyPairCalendarLookupService,
    @Optional() cmp: ClientModelPrivatization,
    @Optional() amp: AccountModelPrivatization,
    @Optional() ctmp: ClientTraderModelPrivatization,
    @Optional() atdlTooltipUiRep: AtdlTooltipUiRep,
    @Optional() sysConfigUtil: BfxSysConfigUtilService,
    @Optional() configService: BfxConfigService,
    @Optional() customFieldLookupService: CustomFieldLookupService,
  ) {
    super(
      profileService,
      rateCache,
      rateSourceLookup,
      myClientLookup,
      myAccountLookup,
      mySalesGroupLookup,
      myTraderGroupLookup,
      ccyLookupService,
      ccyPairLookupService,
      userLookupService,
      sysConfigService,
      translateService,
      myBasePricingService,
      myDealtPricingService,
      fixingSourceLookupService,
      algoStrategyLookupService,
      currencyPairCalendarLookupService,
      cmp,
      amp,
      ctmp,
      atdlTooltipUiRep,
      sysConfigUtil,
      configService,
      customFieldLookupService,
    );

    this.ladderSpotTrackingHelper = new LadderSpotTrackingHelper(
      this.myBasePricingService,
      this.profileService,
    );
  }

  // this override for system configs applicable for blotter definition
  protected getTableCongiPromises(): Array<Promise<any>> {
    return super.getTableCongiPromises();
  }

  protected determineDefaultConfig(defaultConfig: any) {
    super.determineDefaultConfig(defaultConfig);
  }

  // this override to set flags returned by promises
  protected handleTableCongiPromises(values: Array<any>) {
    super.handleTableCongiPromises(values);
  }
  protected getMarketBar(td: TableDefinition): any {
    return null;
  }

  private hideFromGrid(column: BfxColDef): void {
    column.omitFromPicker = true;
    column.hide = true;
  }

  private showInGrid(column: BfxColDef): void {
    column.omitFromPicker = false;
    column.hide = false;
  }

  public formatLadderPair(column: any, params: any): string {
    if (params.data instanceof LadderRate) {
      return "";
    }

    if (column._valueFormatter != null) {
      return column._valueFormatter(params);
    }

    return params.value;
  }

  public createDef(): TableDefinition {
    let def = super.createDef();

    def.primaryKey = "orderId";
    def.columns.forEach((column, index) => {
      column.indexType = IndexType.None;
      column["index"] = index;

      column.sortable = false;

      switch (column.field) {
        case COLUMN_SELECTION:
          break;
        case COLUMN_DEALT_PERCENTAGE:
          column.cellClass = ["blotter_center_align_numeric blotter_priority_col"];
          break;
        case COLUMN_INSTRUMENT:
          column.sortable = true;
          column.sortImmutable = true;
          column.indexType = IndexType.Instrument;

          column.valueGetter = (params) => {
            let base: string = params.data.baseCode;
            let nonBase: string = params.data.nonBaseCode;

            let inverted: boolean = params.context.isInverted(params.data);

            return base + "/" + nonBase + (inverted ? "_inverted" : "");
          };

          column.cellRenderer = function (params) {
            if (params.data instanceof LadderRate) {
              return "";
            }

            let base: string = params.data.baseCode;
            let nonBase: string = params.data.nonBaseCode;

            let value: string = "";
            if (params.context.isInverted(params.data)) {
              value = nonBase + "/" + base;
            } else {
              value = base + "/" + nonBase;
            }

            return value;
          };
          break;
        case COLUMN_DEALT_PERCENTAGE:
          column.disableInPicker = false;
          break;
        case COLUMN_LADDER_POSITION:
          column.omitFromPicker = false;
          break;
        case COLUMN_ORDER_DEALT_TRACKING:
          column.sortable = true;
          column.sortImmutable = true;
          column.cellClass = "blotter_center_align_numeric market-rate blotter_primary_col";
          column.disableInPicker = true;
          let valueGetter: any = column.valueGetter;
          column.minWidth = this.sixWs;
          // column.headerComponentParams = {
          //   template:``
          // }

          column.valueGetter = (params) => {
            if (params.data.constructor.name == LadderRate.name) {
              let ladderRate: LadderRate = params.data;
              let rateCode: string = ladderRate.baseCode + ladderRate.nonBaseCode;
              let rate: Rate = this.rateCache.getRate(rateCode);
              if (rate != null && (CubiqTS.Tenor.SPOT == rate.tenor || rate.ndfHorizon)) {
                let value: string = null;
                let tradeable: boolean = rate.tradable;

                ladderRate.rate = rate;

                if (CubiqTS.Tenor.SPOT == rate.tenor) {
                  value = !ladderRate.isInvert
                    ? rate.marketSpotRateDisplay
                    : rate.invertMarketSpotRateDisplay;
                }

                if (params.context.mode == OrdersTableMode.NDF_BASE) {
                  tradeable = rate.marketNdfRateTradeable;
                  value = !ladderRate.isInvert
                    ? rate.marketNdfRateDisplay
                    : rate.invertMarketNdfRateDisplay;
                }

                if (value != null) {
                  if (!tradeable) {
                    value =
                      '<span class="no-rate-strike">&nbsp;&nbsp;&nbsp' +
                      value +
                      "&nbsp;&nbsp;&nbsp</span>";
                  }
                  return value;
                }
              }
              return "-";
            } else if (params.data.constructor.name == LadderPair.name) {
              return "-";
            }

            // Never write a string into params.data[COLUMN_ORDER_DEALT_TRACKING]
            let value: number;

            if (params.data.constructor.name == LadderAggregate.name) {
              if (params.context.isInverted(params.data)) {
                value = params.data[COLUMN_ORDER_DEALT_TRACKING] =
                  params.data.aggregationInvertTrackingRate;
              } else {
                value = params.data[COLUMN_ORDER_DEALT_TRACKING] =
                  params.data.aggregationTrackingRate;
              }
            } else {
              value = params.data[COLUMN_ORDER_DEALT_TRACKING] =
                PricingService.clearStrikeThroughValue(valueGetter(params));
            }

            this.updateTemperature(params.data, params.context);

            return value;
          };

          column.indexType = IndexType.LadderTracking;
          column.cellRenderer = (params) => {
            if (
              params.data.constructor.name != LadderRate.name &&
              params.data.constructor.name != LadderPair.name
            ) {
              let invertedLadderPair: boolean = params.context.isInverted(params.data);
              let value: string =
                params.value != null && !Number.isNaN(params.value)
                  ? this.myBasePricingService.getRoundedValue(params.data, params.value)
                  : "-";

              if (PricingService.isStrikeThroughValue(value)) {
                value =
                  '<span class="no-rate-strike">' +
                  PricingService.clearStrikeThroughValue(value) +
                  "</span>";
              }

              if (invertedLadderPair != params.data.isInvertedOrderFg) {
                value =
                  '<span style="float: left; position: absolute; left: 2px"><sup>1</sup>/<sub>n</sub></span>' +
                  value;
              }

              return '<div style="width: 100%">' + value + "</div>";
            }

            return params.value;
          };

          column.tooltipValueGetter = (params: ITooltipParams) => "";
          column.showTooltip = false;
          break;
        case OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_AMOUNT:
        case OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTED_AMOUNT_USD:
        case OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_EXECUTED_AMOUNT:
        case OrdersTableDefinitionFactoryConstants.COLUMN_ORDER_USD_AMOUNT:
        case OrdersTableDefinitionFactoryConstants.COLUMN_REMAINING_AMOUNT:
        case OrdersTableDefinitionFactoryConstants.COLUMN_REMAINING_AMOUNT_USD: {
          column.sortable = false;
          if (column.valueGetter != null) {
            column["_valueGetter"] = column.valueGetter;
          } else {
            column["_valueGetter"] = (params) => params.data[column.field];
          }
          column.valueGetter = (params: ValueGetterParams) => {
            if (params.data.isLadderAggregate && params.data[column.field] === null) {
              let value = [...params.data.aggregateOrderIdSet]
                .map((id) =>
                  column["_valueGetter"]({
                    context: params.context,
                    data: params.context.getLimitOrder(id),
                  }),
                )
                .filter((value) => CubiqTS.BigNumber.isBigNumber(value))
                .reduce((a, b, i) => {
                  if (i === 0) {
                    return b;
                  } else {
                    return a.plus(b);
                  }
                }, null);

              params.data[column.field] = value;

              return value;
            } else if (params.data.isLadderAggregate) {
              return params.data[column.field];
            } else {
              return column["_valueGetter"](params);
            }
          };
          if (column.valueFormatter != null) {
            column["_valueFormatter"] = column.valueFormatter;
          }
          column.valueFormatter = this.formatLadderPair.bind(this, column);
          break;
        }
        default:
          column.sortable = false;
          if (column.valueFormatter != null) {
            column["_valueFormatter"] = column.valueFormatter;
          }
          column.valueFormatter = this.formatLadderPair.bind(this, column);
          break;
      }
    });

    return def;
  }

  public updateTemperature(data, context) {
    let invertedLadderPair: boolean = context.isInverted(data);
    if (invertedLadderPair) {
      if (data.tempStale === true) {
        this.basePercentAwayHelper.updateInvertPercentAway(data);
      }
      data[COLUMN_ORDER_DEALT_TRACKING] = this.ladderSpotTrackingHelper.retrieveInvertSortData(
        data,
        data[COLUMN_ORDER_DEALT_TRACKING],
      );
    } else {
      if (data.tempStale === true) {
        this.basePercentAwayHelper.updatePercentAway(data);
      }
      data[COLUMN_ORDER_DEALT_TRACKING] = this.ladderSpotTrackingHelper.retrieveSortData(
        data,
        data[COLUMN_ORDER_DEALT_TRACKING],
      );
    }
  }

  protected getBlotterTypeStr(): string {
    let type: string = super.getBlotterTypeStr();
    return type + "_LADDER";
  }

  public getBlotterType(): BlotterType {
    return BlotterType.ORDER;
  }

  public getAssetClass(): AssetClass {
    return AssetClass.FIX;
  }

  public getDefaultDealtMode(): any {
    return false;
  }

  public getVersionIdentifier(): string {
    return ORDER_LADDER_TABLE_DEFINITION_VER;
  }

  public getAggregationPricingService(): PricingService {
    return this.myBasePricingService;
  }
}
